import React, { useEffect, useState } from "react";
import "./Styles.css";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { BsGithub, BsTwitter } from "react-icons/bs";
import { SlSocialInstagram } from "react-icons/sl";
import { FaFacebookF } from "react-icons/fa";
import { AiOutlineLinkedin } from "react-icons/ai";
import { FiArrowUpRight } from "react-icons/fi";
import About from "./About";
import Experience from "./Experience";
import Skills from "./Skills";
import Project from "./Project";

const EMAIL = "sabinathapa336@gmail.com";

const socials = [
    {
        href: "https://github.com/ardentfiery",
        icon: <BsGithub />,
        label: "GitHub",
    },
    {
        href: "https://www.linkedin.com/in/sabina-thapa41/",
        icon: <AiOutlineLinkedin />,
        label: "LinkedIn",
    },
    {
        href: "https://twitter.com/Sabinakshe",
        icon: <BsTwitter />,
        label: "Twitter",
    },
    {
        href: "https://www.instagram.com/_sabinathapa.04/",
        icon: <SlSocialInstagram />,
        label: "Instagram",
    },
    {
        href: "https://www.facebook.com/sabina.thapakshettry",
        icon: <FaFacebookF />,
        label: "Facebook",
    },
];

const roles = [
    "Frontend Developer",
    "Wordpress Designer",
    "AWS Learner",
    "Data Science Enthusiast",
];

const RotatingRole = () => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(
            () => setIndex((i) => (i + 1) % roles.length),
            2600
        );
        return () => clearInterval(timer);
    }, []);

    return (
        <span className="role-swap">
            <AnimatePresence mode="wait">
                <motion.span
                    key={roles[index]}
                    initial={{ y: 18, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -18, opacity: 0 }}
                    transition={{ duration: 0.35 }}
                >
                    {roles[index]}
                </motion.span>
            </AnimatePresence>
        </span>
    );
};

const Home = () => {
    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, {
        stiffness: 120,
        damping: 30,
    });

    return (
        <div className="page">
            <motion.div className="scroll-progress" style={{ scaleX: progress }} />

            <nav className="navbar">
                <a href="#hero" className="navlogo">
                    Sabina<span className="navdot">.</span>
                </a>
                <div className="navlinks">
                    <a href="#aboutpage">About</a>
                    <a href="#experience">Experience</a>
                    <a href="#skilldiv">Skills</a>
                    <a href="#projectdiv">Projects</a>
                    <a href="#contact" className="nav-cta">
                        Contact
                    </a>
                </div>
            </nav>

            <header className="hero" id="hero">
                <motion.div
                    className="hero-badge"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <span className="pulse-dot" /> Working remotely · Open to
                    collaborations
                </motion.div>
                <motion.h1
                    className="hero-name"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    Sabina Thapa
                </motion.h1>
                <motion.h2
                    className="hero-sub"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <RotatingRole />
                </motion.h2>
                <motion.p
                    className="hero-desc"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    Computer engineer turned frontend specialist. I build
                    responsive, captivating web experiences for clients across
                    Finland, the USA, Hong Kong, and Nepal — currently pursuing
                    my Masters while working remotely.
                </motion.p>
                <motion.div
                    className="hero-actions"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    <a className="btn btn-primary" href="#projectdiv">
                        View My Work <FiArrowUpRight />
                    </a>
                    <a className="btn btn-ghost" href={`mailto:${EMAIL}`}>
                        Get In Touch
                    </a>
                </motion.div>
                <motion.div
                    className="socialmedia"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.55 }}
                >
                    {socials.map((s) => (
                        <a
                            key={s.label}
                            href={s.href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={s.label}
                            className="socials"
                        >
                            {s.icon}
                        </a>
                    ))}
                </motion.div>
                <div className="scroll-cue">
                    <span />
                </div>
            </header>

            <main>
                <About />
                <Experience />
                <Skills />
                <Project />

                <section className="section contact" id="contact">
                    <motion.div
                        className="contact-card"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <p className="contact-kicker">What's next?</p>
                        <h2 className="contact-title">
                            Let's build something together
                        </h2>
                        <p className="contact-text">
                            I work remotely with teams around the world and I'm
                            always excited about new projects, collaborations,
                            and research ideas. Whether you have a product to
                            build or just want to say hi — my inbox is always
                            open.
                        </p>
                        <div className="hero-actions contact-actions">
                            <a
                                className="btn btn-primary"
                                href={`mailto:${EMAIL}`}
                            >
                                Say Hello <FiArrowUpRight />
                            </a>
                            <a
                                className="btn btn-ghost"
                                href="https://www.linkedin.com/in/sabina-thapa41/"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Connect on LinkedIn
                            </a>
                        </div>
                        <p className="contact-email">{EMAIL}</p>
                    </motion.div>
                </section>
            </main>

            <footer className="footer">
                <div className="socialmedia footer-socials">
                    {socials.map((s) => (
                        <a
                            key={s.label}
                            href={s.href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={s.label}
                            className="socials"
                        >
                            {s.icon}
                        </a>
                    ))}
                </div>
                <p>Designed &amp; built with ♥ by Sabina Thapa</p>
            </footer>
        </div>
    );
};

export default Home;
